// SoloStage.tsx - the locked Solo surface (S24). Training wheels off, no prompts. You pick one of the two
// teaching hands, then on every street you: tag the player, weight his hands, LOCK the read (Axis B),
// TYPE both price numbers yourself (to-call + equity %), and choose the play - with NO inline grade. One
// end-of-hand report closes the hand, graded on the read, the price, and the play (not on whether you
// won). The gold hand-picker pill (Hand 1 | Hand 2) sits above the felt.
//
// Reuse: the felt comes from the engine bridge's soloFeltView (per-hand hole cards on a per-hand board -
// the DX9 consistency fix); <ReadLockIn> (tag + weight + lock), <BetMathBuilder> (inside <SoloActionRamp>),
// and <EndOfHandReport> are all reused. The lock lifecycle runs @postflop/engine's lockReducer (revision
// timing captured, never scored - North Star §5).
//
// Two differences from Diagnose: (1) actions are gated on the locked read AND both typed price numbers
// (<SoloActionRamp>), and (2) there is no inline grade - a neutral "logged" confirmation stands between
// streets, and the grade only appears in the report. Deep-linkable at ?mode=solo&street=0..3 (&hand=0|1).
import { useEffect, useMemo, useRef, useState } from 'react';
import { initialLock, lockReducer } from '@postflop/engine';
import type { ReadLock } from '@postflop/schema';
import { OvalFelt } from '../components/OvalFelt';
import { StreetStepper } from '../components/StreetStepper';
import { soloFeltView } from '../engine/bridge';
import { ReadLockIn } from '../diagnose/ReadLockIn';
import type { ActionDef, PlayerTag } from '../diagnose/data';
import { EndOfHandReport } from '../report/EndOfHandReport';
import { SOLO_HANDS } from './data';
import { SoloPriceEntry } from './SoloPriceEntry';
import { SoloActionRamp } from './SoloActionRamp';
import { buildSoloReport, gradeSoloStreet, type SoloResult } from './grade';
import { persistPlayedHand, type PersistableDecision } from '../progress/persistPlay';

type Phase = 'play' | 'logged' | 'report';

const HAND_COUNT = SOLO_HANDS.length;
const LAST = SOLO_HANDS[0].streets.length - 1;

/** Clamp an integer from the URL into [0, max]. */
function clampParam(name: string, max: number): number {
  if (typeof window === 'undefined') return 0;
  const raw = Number(new URLSearchParams(window.location.search).get(name));
  return Number.isInteger(raw) && raw >= 0 && raw <= max ? raw : 0;
}

/** Keep the URL pointed at the current hand + street so both are deep-linkable and shareable. */
function syncUrl(handIdx: number, street: number) {
  if (typeof window === 'undefined') return;
  const q = new URLSearchParams();
  q.set('mode', 'solo');
  q.set('street', String(street));
  if (handIdx !== 0) q.set('hand', String(handIdx));
  window.history.replaceState(null, '', '?' + q.toString());
}

export function SoloStage() {
  const [handIdx, setHandIdx] = useState(() => clampParam('hand', HAND_COUNT - 1));
  const [street, setStreet] = useState(() => clampParam('street', LAST));
  const hand = SOLO_HANDS[handIdx];
  const data = hand.streets[street];
  const felt = useMemo(() => soloFeltView(hand, data.felt), [hand, data.felt]);
  const isRiver = street === LAST;

  // per-street read state (reset each street).
  const [tag, setTag] = useState<PlayerTag | null>(null);
  const [weights, setWeights] = useState<number[]>([]);
  const [lock, setLock] = useState<ReadLock>(initialLock);
  // the two typed price numbers (reset each street; empty while the street has no price).
  const [callTyped, setCallTyped] = useState('');
  const [pctTyped, setPctTyped] = useState('');
  const [phase, setPhase] = useState<Phase>('play');
  // the accumulated per-street answers - the spine of the end-of-hand report. Persists across street
  // navigation within a hand; cleared only on a hand switch or "Play another hand".
  const [results, setResults] = useState<(SoloResult | null)[]>(() => hand.streets.map(() => null));
  // guards the once-per-hand D1 persistence (see the report effect below); reset on hand switch / replay.
  const persistedRef = useRef(false);

  const locked = lock.state === 'lockedIn';
  // the price gate: no price on this street (preflop) is always "ready"; otherwise BOTH numbers must be in.
  const priceReady = !data.price || (callTyped.trim() !== '' && pctTyped.trim() !== '');

  // reset the read + price on every hand/street change (and on first mount for the deep-linked street).
  useEffect(() => {
    setTag(null);
    setWeights(data.families ? data.families.map(() => 0) : []);
    setLock(initialLock());
    setCallTyped('');
    setPctTyped('');
    setPhase('play');
    window.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handIdx, street]);

  // keep the URL pointed at the current hand + street.
  useEffect(() => syncUrl(handIdx, street), [handIdx, street]);

  const go = (i: number) => setStreet(Math.max(0, Math.min(LAST, i)));

  const selectHand = (idx: number) => {
    if (idx === handIdx) return;
    persistedRef.current = false; // a fresh hand logs its own decisions
    setResults(SOLO_HANDS[idx].streets.map(() => null));
    setHandIdx(idx);
    setStreet(0); // the reset effect fires on the handIdx change
  };

  const commit = (key: ActionDef['key'], amount?: string) => {
    const result: SoloResult = {
      tag,
      weights: weights.slice(),
      action: key,
      amount: amount ?? '',
      callTyped: data.price ? callTyped : '',
      pctTyped: data.price ? pctTyped : '',
    };
    setResults((r) => {
      const next = r.slice();
      next[street] = result;
      return next;
    });
    setPhase(isRiver ? 'report' : 'logged');
    window.scrollTo({ top: 0 });
  };

  const playAgain = () => {
    persistedRef.current = false; // the next run logs its own decisions
    setResults(hand.streets.map(() => null));
    setStreet(0); // triggers the reset effect (phase -> play)
    window.scrollTo({ top: 0 });
  };

  // the report reads the four captured answers; any street not played through grades as "No answer".
  const reportModel = useMemo(() => buildSoloReport(results, hand), [results, hand]);

  // Persist the played streets to D1 once the hand closes into the report (Phase 2 "real progress").
  // Solo has no explicit "open report" click (the river commit goes straight to phase 'report'), so this
  // effect is the trigger. Fires once per hand - ref-guarded, reset by playAgain / selectHand - and is
  // best-effort: an offline / Worker-down failure degrades silently (the Progress tab just won't reflect it).
  useEffect(() => {
    if (phase !== 'report' || persistedRef.current) return;
    persistedRef.current = true;
    // Map each street's Solo grade into the surface-neutral shape: the overall grade is the decision grade;
    // the play-rightness and read-rightness become the two skill dimensions.
    const decisions = results.map((r, i): PersistableDecision | null => {
      if (!r) return null;
      const g = gradeSoloStreet(r, hand.streets[i], hand.correctTag);
      const readRight = r.tag === hand.correctTag;
      const actionRight = r.action === hand.streets[i].grade.correct;
      return {
        gradeClass: g.cls,
        skillClasses: {
          value_extraction: actionRight ? 'best' : g.cls,
          player_profiling: readRight ? 'best' : 'inacc',
        },
      };
    });
    void persistPlayedHand(decisions, 'no_tips').then((res) => {
      if (!res.ok) console.warn('[progress] solo persist failed:', res.error);
    });
  }, [phase, results, hand]);

  if (phase === 'report') {
    return (
      <>
        <div className="body">
          <EndOfHandReport model={reportModel} />
        </div>
        <div className="dock">
          <div className="dockrow">
            <button type="button" className="cta ghost prev" onClick={() => setPhase('play')}>
              Back
            </button>
            <button type="button" className="cta" onClick={playAgain}>
              Play another hand
            </button>
          </div>
        </div>
      </>
    );
  }

  const loggedSub = data.price
    ? 'Your read, your price, and your play are in. You will see how they scored at the end of the hand.'
    : 'Your read and your play are in. You will see how they scored at the end of the hand.';

  return (
    <>
      <OvalFelt view={felt} />

      <div className="body">
        {/* the gold hand-picker pill (Hand 1 | Hand 2) */}
        <div className="handpick">
          {SOLO_HANDS.map((h, i) => (
            <button
              key={h.pick.k}
              type="button"
              className={'hp' + (i === handIdx ? ' on' : '')}
              onClick={() => selectHand(i)}
            >
              <span className="k">{h.pick.k}</span>
              {h.pick.label}
            </button>
          ))}
        </div>

        <div>
          <div className="kicker">{data.kicker}</div>
          <div className="h2">{data.h2}</div>
          <div className="lede">{data.lede}</div>
        </div>

        <StreetStepper street={street} onSelect={go} />

        {phase === 'play' ? (
          <>
            <ReadLockIn
              tagSub={data.tagSub}
              families={data.families}
              tag={tag}
              weights={weights}
              locked={locked}
              onTag={setTag}
              onCycleWeight={(i) =>
                setWeights((w) => w.map((v, j) => (j === i ? (v + 1) % 4 : v)))
              }
              onLock={() => setLock((l) => lockReducer(l, { type: 'lockIn', atDecisionIndex: street }))}
              onRevise={() => setLock((l) => lockReducer(l, { type: 'edit' }))}
            />
            {/* the price entry appears only after the read is locked, and only when there is a price. */}
            {locked && data.price && (
              <SoloPriceEntry
                pot={data.felt.pot ? `$${data.felt.pot}` : '$0'}
                hisBet={data.felt.vbet}
                call={callTyped}
                pct={pctTyped}
                onCall={setCallTyped}
                onPct={setPctTyped}
              />
            )}
          </>
        ) : (
          <div className="solog">
            <div className="t">Street logged</div>
            <div className="s">{loggedSub}</div>
            <div className="pend">
              {results.map((r, k) => (
                <i key={k} className={r ? 'done' : ''} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="dock">
        {phase === 'play' ? (
          <SoloActionRamp
            actions={data.actions}
            bet={data.bet}
            locked={locked}
            priceReady={priceReady}
            onCommit={commit}
          />
        ) : (
          <div className="dockrow">
            <button type="button" className="cta ghost prev" onClick={() => setPhase('play')}>
              Revise
            </button>
            <button type="button" className="cta" onClick={() => go(street + 1)}>
              {/* streets[street+1] is briefly undefined on the turn->river transition (this logged dock
                  renders once before the reset effect flips phase to 'play'); guard so it never throws. */}
              Next: the {hand.streets[street + 1]?.id ?? 'next street'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
